import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// New authentic Ted's Shades images
import romanShadeKitchenImg from '@/assets/roman-shade-kitchen.webp';
import shuttersCloseupImg from '@/assets/shutters-closeup.webp';
import cellularShadesWhiteImg from '@/assets/cellular-shades-white.webp';
import rollerShadesImg from '@/assets/roller-shades.jpg';
import sunScreenImg from '@/assets/sun-screen.webp';
import rollerShadesSlidingDoorsImg from '@/assets/roller-shades-sliding-doors.webp';
import fauxWoodBlindImg from '@/assets/faux-wood-blind.webp';
import woodBlindsImg from '@/assets/wood-blinds.jpg';
import patioShadePoolImg from '@/assets/patio-shade-peoria-pool.jpg';
import zebraShadesImg from '@/assets/zebra-shades.webp';
import smartDrapesImg from '@/assets/smart-drapes.webp';

const products = [
  {
    id: 11,
    name: 'Patio Shades',
    description: 'Durable outdoor shades that block Arizona sun, reduce heat, and extend your outdoor living space.',
    image: patioShadePoolImg,
    features: ['UV & heat protection', 'Weather-resistant materials', 'Motorized options available'],
    link: '/patio-shades',
  },
  {
    id: 1,
    name: 'Roman Shades',
    description: 'Timeless elegance with soft, cascading folds. Perfect for traditional and contemporary interiors.',
    image: romanShadeKitchenImg,
    features: ['Custom fabrics & colors', 'Cordless & motorized options', 'Light filtering to blackout'],
    link: '/roman-shades',
  },
  {
    id: 2,
    name: 'Plantation Wood Shutters',
    description: 'Premium hardwood shutters with unmatched warmth, elegance, and architectural character.',
    image: shuttersCloseupImg,
    features: ['Premium hardwoods', 'Increases home value', 'Precise light control'],
    link: '/plantation-wood-shutters',
  },
  {
    id: 3,
    name: 'Cellular Shades',
    description: 'Honeycomb design traps air for superior insulation and energy savings.',
    image: cellularShadesWhiteImg,
    features: ['Best for energy efficiency', 'Light filtering options', 'Quiet operation'],
    link: '/cellular-shades',
  },
  {
    id: 4,
    name: 'Roller Shades',
    description: 'Clean, modern look with smooth operation. Available in various opacities.',
    image: rollerShadesImg,
    features: ['Sleek minimal design', 'Easy to clean', 'Smart home compatible'],
    link: '/roller-shades',
  },
  {
    id: 5,
    name: 'Sun Screens',
    description: 'Block harsh Arizona sun while maintaining your view. Perfect for patios.',
    image: sunScreenImg,
    features: ['UV protection', 'Heat reduction', 'Preserves outdoor view'],
    link: '/sun-screens',
  },
  {
    id: 6,
    name: 'Motorized Shades',
    description: 'Modern convenience with smart home integration. Control with a touch or automate schedules.',
    image: rollerShadesSlidingDoorsImg,
    features: ['Smart home compatible', 'Automated schedules', 'Cordless & safe'],
    link: '/motorized-shades',
    imageClassName: '-rotate-90',
  },
  {
    id: 7,
    name: 'Smart Drapes',
    description: 'Elegant custom drapery with advanced automation for large windows and sliding doors.',
    image: smartDrapesImg,
    features: ['Remote & app control', 'Cordless & elegant', 'Smart home integration'],
    link: '/smart-drapes',
  },
  {
    id: 8,
    name: 'Zebra Shades',
    description: 'Modern dual shades with alternating bands for precise light and privacy control.',
    image: zebraShadesImg,
    features: ['Dual-layer design', 'Contemporary style', 'Easy light transition'],
    link: '/zebra-shades',
  },
  {
    id: 9,
    name: 'Faux Wood Blinds',
    description: 'Classic wood look with enhanced durability and moisture resistance for Arizona homes.',
    image: fauxWoodBlindImg,
    features: ['Moisture resistant', 'Easy maintenance', 'Variety of finishes'],
    link: '/faux-wood-blinds',
  },
  {
    id: 10,
    name: 'Wood Blinds',
    description: 'Natural warmth and character crafted from high-quality hardwood for timeless elegance.',
    image: woodBlindsImg,
    features: ['Premium hardwood', 'Precise light control', 'Timeless appearance'],
    link: '/wood-blinds',
  },
];

export function Products() {
  return (
    <section id="products" className="py-20 md:py-28 bg-secondary/30">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 bg-sage-light text-sage rounded-full text-sm font-medium mb-4">
            Our Products
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Custom Window Treatments for Every Style
          </h2>
          <p className="text-lg text-muted-foreground">
            From classic shutters to modern roller shades, we offer a wide variety of 
            high-quality window coverings tailored to your home's unique needs.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {products.map((product, index) => (
            <article
              key={product.id}
              className={`group bg-card rounded-xl overflow-hidden shadow-soft hover:shadow-medium transition-all duration-300 ${
                index === 0 ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
            >
              <Link to={product.link} className="aspect-square overflow-hidden block cursor-pointer">
                <img
                  src={product.image}
                  alt={product.name}
                  className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${(product as any).imageClassName || ''}`}
                  loading="lazy"
                  width={384}
                  height={384}
                />
              </Link>
              <div className="p-6">
                <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                  {product.name}
                </h3>
                <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                  {product.description}
                </p>
                <ul className="space-y-2 mb-4">
                  {product.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-foreground/80">
                      <span className="w-1.5 h-1.5 rounded-full bg-sage" />
                      {feature}
                    </li>
                  ))}
                </ul>
                {product.link.startsWith('/') ? (
                  <Link
                    to={product.link}
                    className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-accent/80 transition-colors group/link"
                  >
                    Learn More About {product.name}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                  </Link>
                ) : (
                  <a
                    href={product.link}
                    className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-accent/80 transition-colors group/link"
                  >
                    Learn More About {product.name}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
